import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { Avatar, Box, Button, Chip, Paper, SvgIcon, Tooltip, Typography } from '@mui/material';

import IconEye from 'app/ui-kit/icons/EyeOpen.svg';
import IconTwitter from 'app/ui-kit/icons/Twitter.svg';
import useStores from 'hooks/use-stores';
import { renderMd } from 'pages/Search/components/SearchItem/utils';
import formatDate from 'utils/format-date';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';
import Utils from 'utils/Utils';

import ExactMatchList from './components/ExactMatchList';
import Score from './components/Score';
import WildExploited from './components/WildExploited';
import useStyles from './styles';

const SearchItem = ({ item, exactMatch, hits }) => {
    const { classes, cx } = useStyles();
    const history = useHistory();
    const { robotTypesStore } = useStores();
    const { type } = item;
    const location = useLocation();

    const items = useMemo(() => {
        const output = hits.total
            ? hits.hits.map(({ _source, flatDescription }) => ({ flatDescription, ..._source }))
            : [item];

        return output.map(({ description, flatDescription, ...rest }) => ({
            ...rest,
            description: renderMd(description || ''),
            flatDescription: renderMd(flatDescription || '')
        }));
    }, [hits.hits, hits.total, item]);

    const isMultipleItems = items.length > 1;

    const hitsHref = useMemo(() => {
        const query = new URLSearchParams(location.search).get('query');
        return `/search?query=${query} AND type:${type}`;
    }, [location.search, type]);

    const handleClick = (id, type, isAuxClick) => (event) => {
        const isCtrlOrCmd = event.metaKey || event.ctrlKey || isAuxClick;
        isCtrlOrCmd ? window.open(`/${type}/${id}`, '_blank') : history.push(`/${type}/${id}`);
    };

    return (
        <Paper
            elevation={0}
            className={cx(classes.item, {
                [classes.exactMatchItem]: !!exactMatch,
                [classes.singleItem]: !isMultipleItems
            })}
        >
            {isMultipleItems && (
                <Box className={classes.header}>
                    <Box className={classes.avatarContainer}>
                        <Avatar className={classes.avatar} src={Utils.getImage(type)} />
                        <Box>{robotTypesStore.get(type)}</Box>
                    </Box>
                    {!!hits.total && (
                        <Button className={classes.btnMore} variant="contained" href={hitsHref}>
                            {hits.total}
                        </Button>
                    )}
                </Box>
            )}
            <Box className={classes.list}>
                {items.map(
                    ({
                        id,
                        title,
                        flatDescription,
                        description,
                        published,
                        cvss,
                        cvss3,
                        epss,
                        viewCount,
                        enchantments,
                        bounty
                    }) => (
                        <Box key={id} className={classes.content}>
                            {!isMultipleItems && (
                                <Box className={classes.avatarContainer}>
                                    <Avatar className={classes.avatar} src={Utils.getImage(type)} alt={type} />
                                </Box>
                            )}
                            <Box flex={1}>
                                <div
                                    className={classes.contentText}
                                    onClick={handleClick(id, type)}
                                    onAuxClick={handleClick(id, type, true)}
                                >
                                    <Box className={classes.contentType}>{robotTypesStore.get(type)}</Box>
                                    <Typography variant="body2" className={classes.title}>
                                        <Link
                                            className={classes.link}
                                            to={`/${type}/${id}`}
                                            onClick={(event) => event.stopPropagation()}
                                            href={`/${type}/${id}`}
                                        >
                                            {title}
                                        </Link>
                                    </Typography>
                                    <Typography variant="body2" className={classes.description}>
                                        {Utils.trunc(300, flatDescription || description)}
                                    </Typography>
                                </div>
                                <Box className={classes.chips}>
                                    {!!cvss3 && !!cvss3.cvssV3 && !!cvss3.cvssV3.baseScore && (
                                        <Score score={cvss3.cvssV3.baseScore} className={classes.score} />
                                    )}
                                    {!isEmpty(enchantments?.vulnersScore || enchantments?.score?.value) && (
                                        <Score
                                            name="AI Score"
                                            score={enchantments?.vulnersScore || enchantments?.score?.value}
                                            className={classes.score}
                                        />
                                    )}
                                    {!isEmpty(epss) && (
                                        <Score
                                            name="EPSS"
                                            score={Number(
                                                toMeaningfulFixed(Math.max(...epss.map(({ epss }) => Number(epss))), 3)
                                            )}
                                            precision={0.1}
                                            className={classes.score}
                                        />
                                    )}
                                    {!cvss3 && !!cvss && !!cvss.score && (
                                        <Score score={cvss.score} className={classes.score} />
                                    )}
                                    <Chip
                                        className={classes.chip}
                                        label={formatDate(published, 'yyyy-MM-dd hh:mm a')}
                                    />
                                    {!!viewCount && (
                                        <Tooltip title="View Count">
                                            <div>
                                                <Chip
                                                    label={viewCount}
                                                    className={classes.chip}
                                                    icon={<SvgIcon className={classes.chipIcon} component={IconEye} />}
                                                />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {!!bounty && (
                                        <Tooltip title="Bounty Amount">
                                            <div>
                                                <Chip label={`$${bounty}`} className={classes.chip} />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {!!enchantments?.exploitation?.wildExploited && (
                                        <WildExploited {...enchantments.exploitation} className={classes.chip} />
                                    )}
                                    {!!enchantments && !!enchantments.twitter && !!enchantments.twitter.counter && (
                                        <Tooltip title="Tweets">
                                            <div>
                                                <Chip
                                                    label={enchantments.twitter.counter}
                                                    className={classes.chip}
                                                    icon={
                                                        <SvgIcon className={classes.chipIcon} component={IconTwitter} />
                                                    }
                                                />
                                            </div>
                                        </Tooltip>
                                    )}
                                </Box>
                                {exactMatch && <ExactMatchList data={exactMatch._source} />}
                            </Box>
                        </Box>
                    )
                )}
            </Box>
        </Paper>
    );
};

SearchItem.propTypes = {
    item: PropTypes.object,
    exactMatch: PropTypes.object,
    hits: PropTypes.object
};

SearchItem.defaultProps = {
    hits: {}
};

export default observer(SearchItem);
